/**
 * Cart UI bridge — minimal GoKwik sync (do not call GoKwik SDK update/refresh manually).
 * GoKwik side-cart.js tracks cart via its own fetch interceptors on /cart/add.js.
 */

async function fetchCartJson() {
  const cartUrl = window.routes?.cart_url || '/cart';
  const response = await fetch(`${cartUrl}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Could not fetch cart');
  return response.json();
}

function isGokwikActive() {
  return (
    typeof window.gokwikSdk !== 'undefined' ||
    typeof window.merchantInfo !== 'undefined' ||
    typeof window.kwikCart !== 'undefined' ||
    typeof window.GokwikCart !== 'undefined' ||
    document.querySelector(
      '[class*="gokwik"], [id*="gokwik"], [class*="Gokwik"], [id*="Gokwik"], [class*="kwik-cart"], [id*="kwik-cart"], side-cart'
    )
  );
}

function guardDawnCartDrawerRender() {
  const patchRenderContents = (target) => {
    if (!target || target.__packRenderGuardApplied || typeof target.renderContents !== 'function') {
      return;
    }

    const originalRenderContents = target.renderContents;

    target.renderContents = function renderContentsGuard(parsedState) {
      if (!parsedState?.sections || typeof parsedState.sections !== 'object') {
        return;
      }

      return originalRenderContents.call(this, parsedState);
    };

    target.__packRenderGuardApplied = true;
  };

  patchRenderContents(document.querySelector('cart-drawer'));

  const CartDrawerElement = customElements.get('cart-drawer');
  if (CartDrawerElement?.prototype) {
    patchRenderContents(CartDrawerElement.prototype);
  }
}

function notifyGokwikCartRefresh() {
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

function openGokwikSidebarCart() {
  try {
    if (typeof window.gokwikSdk?.openSidebarCart === 'function') {
      window.gokwikSdk.openSidebarCart();
      return true;
    }
    if (typeof window.gokwikSdk?.openCart === 'function') {
      window.gokwikSdk.openCart();
      return true;
    }
  } catch (error) {
    /* GoKwik optional */
  }

  return false;
}

function dispatchNativeCartEvents(cart, { openDrawer = true } = {}) {
  const detail = { cart, openMiniCart: openDrawer, open: openDrawer };

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:refresh', { bubbles: true, detail })
  );
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
  );
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data: { itemCount: cart?.item_count, source: 'pack-selector' } },
    })
  );
}

async function refreshGokwikCart(options = {}) {
  const { openDrawer = true } = options;

  notifyGokwikCartRefresh();

  if (openDrawer) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    openGokwikSidebarCart();
  }

  if (typeof window.applyGokwikPackGrouping === 'function') {
    window.applyGokwikPackGrouping();
  }
}

async function refreshNativeCartDrawer({ openDrawer = true } = {}) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return false;

  try {
    const cartUrl = window.routes?.cart_url || '/cart';
    const sectionId = cartDrawer.dataset.sectionId || 'cart-drawer';
    const response = await fetch(`${cartUrl}?section_id=${sectionId}`);
    if (!response.ok) return false;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');
    if (!newDrawer) return false;

    cartDrawer.innerHTML = newDrawer.innerHTML;
    cartDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));

    if (openDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('active', 'animate');
      }
    }

    return true;
  } catch (error) {
    console.warn('Native cart drawer refresh failed', error);
    return false;
  }
}

async function refreshCartUI(options = {}) {
  const { openDrawer = true } = options;

  if (isGokwikActive()) {
    await refreshGokwikCart({ openDrawer });
    return;
  }

  let cart;

  try {
    cart = await fetchCartJson();
  } catch (error) {
    console.warn('Could not fetch cart after add', error);
  }

  dispatchNativeCartEvents(cart, { openDrawer });

  const refreshed = await refreshNativeCartDrawer({ openDrawer });
  if (!refreshed && openDrawer && !document.querySelector('cart-drawer')) {
    window.location.href = window.routes?.cart_url || '/cart';
  }
}

window.refreshCartUI = refreshCartUI;
window.refreshCartDrawer = () => refreshCartUI({ openDrawer: false });
window.isGokwikCartActive = isGokwikActive;
window.notifyGokwikCartRefresh = notifyGokwikCartRefresh;
window.openGokwikSidebarCart = openGokwikSidebarCart;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', guardDawnCartDrawerRender);
} else {
  guardDawnCartDrawerRender();
}

if (window.customElements?.whenDefined) {
  customElements.whenDefined('cart-drawer').then(guardDawnCartDrawerRender).catch(() => {});
}

document.addEventListener('shopify:section:load', guardDawnCartDrawerRender);